<template>
	<div>
		<div v-if="showButtons">
			<div>
				<Input
					labelText="Labor Rate Id (set to 999 for no results)"
					v-model="laborRateIdData"
					width="200"
				/>
				<ButtonClassic
					labelText="Mismatch Modal"
					@click="onMismatchModalClick"
					ref="mismatchModalButton"
				/>
			</div>
			<div>
				<ButtonClassic
					labelText="Recalc Matrix Confirmation Modal"
					@click="onRecalcMatrixClick"
					ref="recalcButton"
				/>
			</div>
			<div>
				<ButtonClassic
					labelText="Mismatch Toast Modal"
					@click="onMismatchToastClick"
					ref="toastButton"
				/>
			</div>
		</div>
		<LaborRateMismatchModal
			v-if="currentModal === modals.mismatch"
			:laborRateId="parseInt(laborRateIdData)"
			@close="onMismatchModalClose"
			@openWindow="onOpenWindow"
			@opCodeUpdateStart="onOpCodeUpdateStart"
			@opCodeUpdateDone="onOpCodeUpdateDone"
		/>
		<UpdateProcessingModal
			v-if="opCodesProcessing"
			:visible="opCodesProcessing"
			title="Op Codes Updating"
			updatingMessage="Op Codes Updating"
		>
			<template v-slot:message>
				Op Codes data is currently being updated.
				<br />
				Please wait to view information after changes are finished
				updating.
			</template>
		</UpdateProcessingModal>
		<RecalcMatrixConfirmationModal
			v-if="currentModal === modals.recalcMatrix"
			:visible="currentModal === modals.recalcMatrix"
			@close="onRecalcMatrixClose"
			@recalc="onRecalculateMatrix"
		/>
		<Toast
			v-if="currentModal === modals.mismatchToast"
			class="notification-list__toast-item"
			:visible="true"
			:state="'info'"
			:iconClassName="'icon-info'"
			:dismissibleTime="10"
			:siteLevel="true"
			:slim="true"
			:title="'Labor Rate Saved'"
			:subtitle="'Rate Mismatch in Open ROs/Op Codes'"
			@close="onMismatchToastClose"
			@removed="onMismatchToastClose"
		>
			<template v-slot:bodyMsg>
				<div
					v-html="
						'<p>The updated labor rate is linked to ROs</br>and/or Op Codes with different rates.</br>Update manually if needed.</p>'
					"
				></div>
			</template>
			<template v-slot:bodyAction>
				<ButtonClassic
					@click="onMismatchModalClick"
					labelText="View Mismatch List"
					:iconLeading="true"
					iconLeadingClassName="icon-list"
				/>
			</template>
		</Toast>
	</div>
</template>

<script>
import Input from '@/components/forms/input/input.vue';
import ButtonClassic from '@/components/forms/button/button-classic.vue';
import LaborRateMismatchModal from './labor-rate-mismatch-modal.vue';
import pageMixin from '@/mixins/page-mixin';
import messageHandlerMixin from '@/mixins/message-handler-mixin';
import UpdateProcessingModal from '@/site-components/modals/update-processing-modal.vue';
import RecalcMatrixConfirmationModal from './recalc-matrix-confirmation-modal.vue';
import Toast from '@/components/notifications/toast/toast.vue';
import { mapGetters } from 'vuex';

export default {
	name: 'LaborRatePage',
	metaInfo: {
		title: 'Labor Rate'
	},
	mixins: [pageMixin, messageHandlerMixin],
	components: {
		Input,
		ButtonClassic,
		LaborRateMismatchModal,
		UpdateProcessingModal,
		RecalcMatrixConfirmationModal,
		Toast
	},
	data() {
		return {
			mismatchModalActive: false,
			laborRateIdData: this.laborRateId,
			modals: {
				mismatch: 'mm',
				recalcMatrix: 'rm',
				mismatchToast: 'mt'
			},
			currentModal: undefined,
			opCodesProcessing: false
		};
	},
	props: {
		laborRateId: {
			type: Number,
			default: 1
		},
		/**
		 * tells page which modal to open
		 * mm = mismatch modal
		 * uc = update confirmation
		 */
		modal: {
			type: String
		},
		/***
		 * tells page this is being iframed
		 */
		frame: {
			type: Boolean
		}
	},
	created() {
		if (!this.frame) {
			this.hidePageLoader();
		} else {
			if (this.modal === this.modals.mismatchToast) {
				this.hidePageLoader();
			} else {
				this.showPageLoader();
			}

			this.sendPageReadyMessage();
			this.showModal(this.modal);
		}
	},
	computed: {
		...mapGetters('laborRates', [
			'opCodeMismatchCount',
			'operationMismatchesCount'
		]),
		showButtons() {
			return !this.modal && !this.frame;
		}
	},
	methods: {
		onModalMessage(message) {
			this.modalData = message.options;
			this.showModal(message.modal);
		},
		showModal(modal) {
			switch (modal) {
				case this.modals.mismatch:
					//mm = LaborRateMismatchModal
					this.currentModal = this.modals.mismatch;
					this.onMismatchModalClick();
					break;
				case this.modals.recalcMatrix:
					//uc = recalcMatrixModal
					this.currentModal = this.modals.recalcMatrix;
					this.onRecalcMatrixClick();
					break;
				case this.modals.mismatchToast:
					//mt = Mismatch Toast Modal
					this.currentModal = this.modals.mismatchToast;
					this.onMismatchToastClick();
					break;
				default:
					break;
			}
		},
		onMismatchModalClick() {
			this.showPageLoader();
			this.currentModal = this.modals.mismatch;
		},
		onMismatchModalClose() {
			this.sendCloseMessageToAccess();
			this.currentModal = undefined;
		},
		onRecalcMatrixClick() {
			this.currentModal = this.modals.recalcMatrix;
		},
		onMismatchToastClick() {
			this.currentModal = this.modals.mismatchToast;
		},
		onRecalcMatrixClose() {
			this.sendCloseMessageToAccess();
			this.currentModal = undefined;
		},
		onMismatchToastClose() {
			this.sendCloseMessageToAccess();
			this.currentModal = undefined;
		},
		sendPageReadyMessage() {
			this.postIFrameReadyMessageToAccess();
		},
		sendCloseMessageToAccess() {
			const currentModal = this.currentModal;
			this.currentModal = undefined;
			this.postCloseMessageToAccess();
			this.postCustomEventMessageToAccess('mismatch-modal-closed', {
				currentModal: currentModal,
				opCodeMismatchCount: this.opCodeMismatchCount,
				operationMismatchesCount: this.operationMismatchesCount
			});
		},
		onOpenWindow({ url, target, windowFeatures }) {
			this.postOpenWindowMessageToAccess({ url, target, windowFeatures });
		},
		onOpCodeUpdateStart() {
			this.opCodesProcessing = true;
		},
		onOpCodeUpdateDone() {
			this.opCodesProcessing = false;
		},
		onRecalculateMatrix(recalcAll) {
			this.postCustomEventMessageToAccess('labor-rate-matrix-recalc', {
				recalcAll
			});

			this.sendCloseMessageToAccess();
		}
	}
};
</script>

<style></style>
